import {
  DEFAULT_HARDWARE_CHECKOUT_COUNTRY,
  isOtherShippingCountry,
} from "../../shippingCountries";
import type { PixiWizardSelection } from "../../hooks/usePixiWizardState";
import {
  BillingCycle,
  CurrencyCode,
  DealId,
  dealEligibility,
  DEFAULT_BILLING_CYCLE,
  DEFAULT_CURRENCY,
  DEFAULT_PLAN,
  PlanId,
} from "./constants";

export interface ResolvedSelection {
  planId: PlanId;
  currency: CurrencyCode;
  billingCycle: BillingCycle;
  shippingCountry: string;
  licenses: number;
}

/**
 * The single source of truth for the Plan & Billing selection: applies the
 * defaults and the Pro-trialist override (always Pro). Used by both the step
 * (cards/deals) and the wizard footer so they can never disagree.
 */
export const resolveSelection = (
  state: PixiWizardSelection,
  isProTrialist: boolean,
): ResolvedSelection => ({
  planId: isProTrialist ? "pro" : (state.planId ?? DEFAULT_PLAN),
  currency: state.currency ?? DEFAULT_CURRENCY,
  billingCycle: state.billingCycle ?? DEFAULT_BILLING_CYCLE,
  shippingCountry: state.shippingCountry ?? DEFAULT_HARDWARE_CHECKOUT_COUNTRY,
  licenses: state.screenCount,
});

/**
 * Whether the free-device deal can be chosen for the current selection: deals
 * must be available for the billing cycle (Yearly only) AND the "Ship to"
 * country must be one we can ship hardware to. Selecting the FE-only `Other`
 * option makes it unavailable (GRWBKK-1730).
 */
export const isFreeDeviceAvailable = (
  billingCycle: BillingCycle,
  shippingCountry: string,
): boolean =>
  dealEligibility(billingCycle).freeDeviceAvailable &&
  !isOtherShippingCountry(shippingCountry);

/**
 * The *valid* deal for the current selection — the single source of truth for
 * pricing, the footer total, deal highlighting and Continue gating, so every
 * consumer derives from a valid deal rather than the raw persisted `dealId`
 * (GRWBKK-1730). It strips the two selections that invalidate `free-device`.
 * A "Ship to" of the unsupported `Other` option falls back to `discount`, which
 * is always offered alongside free-device (i.e. Yearly). Monthly billing, where
 * the free device isn't offered at all (`isFreeDeviceAvailable`), falls back to
 * no deal — nothing is invented in its place, since Monthly's own deal state is
 * "none", which is what selecting Monthly writes. That case is only reachable on
 * a hard resume from localStorage, as switching cycle already drops the deal.
 *
 * Every other selection — including an unset deal, the discount deal and the
 * Monthly + discount pricing path (GRWBKK-1705) — is passed through untouched.
 */
export const resolveEffectiveDealId = (
  state: PixiWizardSelection,
): DealId | undefined => {
  const shippingCountry =
    state.shippingCountry ?? DEFAULT_HARDWARE_CHECKOUT_COUNTRY;
  const billingCycle = state.billingCycle ?? DEFAULT_BILLING_CYCLE;
  if (state.dealId === "free-device") {
    if (!dealEligibility(billingCycle).freeDeviceAvailable) {
      return undefined;
    }
    if (isOtherShippingCountry(shippingCountry)) {
      return "discount";
    }
  }
  return state.dealId;
};

/**
 * Whether the selection ships physical hardware, and so needs a shipping
 * address, a shipping method and a shipping charge. The free device is the only
 * hardware in the flow, so this is exactly "the valid deal is free-device" —
 * which also makes Monthly shipping-free, since the free device is Yearly-only.
 *
 * The single source of truth for every shipping gate: the Payment & Shipping
 * step's form + validation, the Continue address verification and the Checkout
 * review card. They derived it independently before and disagreed
 * (GRWBKK-1735) — a "no deal" selection is not a shipping selection.
 */
export const shipsHardware = (state: PixiWizardSelection): boolean =>
  resolveEffectiveDealId(state) === "free-device";
